import { useEffect, useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";
const RAD = Math.PI / 180;

// SVG orthographic (globe) projection — reliable on every device, no WebGL.
// Slowly spins; highlights visited countries and shows front-facing pins.
export default function TripGlobe({ markers = [], origin = null, size = 260, className = "" }) {
  const pts = useMemo(
    () => markers.filter((m) => typeof m.lat === "number" && typeof m.lng === "number"),
    [markers]
  );
  const originPin = origin && typeof origin.lat === "number" && typeof origin.lng === "number" ? origin : null;

  const center = useMemo(() => {
    const all = [...pts, ...(originPin ? [originPin] : [])];
    if (!all.length) return { lat: 20, lng: 0 };
    const lat = all.reduce((s, m) => s + m.lat, 0) / all.length;
    const lng = all.reduce((s, m) => s + m.lng, 0) / all.length;
    return { lat, lng };
  }, [pts, originPin]);

  const [lambda, setLambda] = useState(-center.lng);
  useEffect(() => { setLambda(-center.lng); }, [center.lng]);
  useEffect(() => {
    const id = setInterval(() => setLambda((l) => l - 0.25), 60);
    return () => clearInterval(id);
  }, []);

  const visited = useMemo(() => {
    const norm = (s) => (s || "").replace(/[^\p{L}\s]/gu, "").trim().toLowerCase();
    const s = new Set(markers.map((m) => norm(m.name)));
    if (originPin?.country) s.add(norm(originPin.country));
    return s;
  }, [markers, originPin]);
  const centerLng = -lambda;

  const isFront = (lat, lng) => {
    const c =
      Math.sin(center.lat * RAD) * Math.sin(lat * RAD) +
      Math.cos(center.lat * RAD) * Math.cos(lat * RAD) * Math.cos((lng - centerLng) * RAD);
    return c > 0.04;
  };

  const scale = size / 2 - 2;

  return (
    <div className={className} style={{ width: size, height: size, maxWidth: "100%" }} data-testid="trip-globe">
      <ComposableMap
        width={size}
        height={size}
        projection="geoOrthographic"
        projectionConfig={{ rotate: [lambda, -center.lat, 0], scale }}
        style={{ width: "100%", height: "100%" }}
      >
        <defs>
          <radialGradient id="globe-ocean" cx="38%" cy="32%" r="75%">
            <stop offset="0%" stopColor="#1a6f9e" />
            <stop offset="70%" stopColor="#0d4a70" />
            <stop offset="100%" stopColor="#062f49" />
          </radialGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={scale} fill="url(#globe-ocean)" stroke="rgba(255,255,255,0.15)" strokeWidth={1} />
        <Geographies geography={GEO_URL}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const isVisited = visited.has((geo.properties.name || "").toLowerCase());
              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  style={{
                    default: { fill: isVisited ? "var(--tenant-accent)" : "#8fb4c7", stroke: "#0d4a70", strokeWidth: 0.3, outline: "none", opacity: isVisited ? 1 : 0.9 },
                    hover: { fill: isVisited ? "var(--tenant-accent)" : "#8fb4c7", outline: "none" },
                    pressed: { outline: "none" },
                  }}
                />
              );
            })
          }
        </Geographies>
        {pts.map((m, idx) =>
          isFront(m.lat, m.lng) ? (
            <Marker key={idx} coordinates={[m.lng, m.lat]}>
              {m.current && (
                <circle r={9} fill="var(--tenant-accent)" opacity={0.3}>
                  <animate attributeName="r" from="5" to="14" dur="1.6s" repeatCount="indefinite" />
                  <animate attributeName="opacity" from="0.4" to="0" dur="1.6s" repeatCount="indefinite" />
                </circle>
              )}
              <circle r={5} fill={m.current ? "var(--tenant-accent)" : "#ffffff"} stroke={m.current ? "#fff" : "var(--tenant-accent)"} strokeWidth={1.6} />
            </Marker>
          ) : null
        )}
        {originPin && isFront(originPin.lat, originPin.lng) && (
          <Marker coordinates={[originPin.lng, originPin.lat]}>
            <circle r={6} fill="#2C2C3E" stroke="#fff" strokeWidth={1.6} />
            <path d="M -2.6 0.4 L 0 -2.4 L 2.6 0.4 L 2.6 3 L -2.6 3 Z" fill="#fff" />
          </Marker>
        )}
      </ComposableMap>
    </div>
  );
}
